import { animalSpeciesHelper, inspectionLevelHelper } from '@farmshare/utils';
import { alpha, Box, Card, Stack, Typography, useTheme } from '@mui/material';
import moment from 'moment';

import {
  formatPhoneDisplay,
  isPhoneNumberValid,
} from 'features/customers/helpers/formatting';
import { ProducerJobByIdQuery } from 'lib/graphql';

import { STATUS_DISPLAY } from '../../shared/styles';
import { getLatestKillDate } from '../../utils/get-latest-kill-date';

type Job = NonNullable<ProducerJobByIdQuery['processorSchedulingById']>;

export function JobInfoCard({ job }: { job: Job }) {
  const theme = useTheme();

  const statusLabel = STATUS_DISPLAY[job.status] ?? job.status;
  const species = animalSpeciesHelper(job.animalSpecies).label;
  const dropoff = job.dropoffDate
    ? moment(job.dropoffDate).utc().format('MMM D, YYYY')
    : null;
  const latestKillDate = getLatestKillDate(job.animalHeads);
  const killDate = latestKillDate
    ? moment(latestKillDate).utc().format('MMM D, YYYY')
    : null;
  const cutDate = job.animalHeads?.[0]?.cutDate
    ? moment(job.animalHeads[0].cutDate).utc().format('MMM D, YYYY')
    : null;
  const successColor = theme.palette.success.main;
  const phone = job.vendor?.address?.phone;
  const formattedPhone = isPhoneNumberValid(phone)
    ? formatPhoneDisplay(phone)
    : null;
  const inspectionLevel = job.animalHeads?.[0]?.inspectionLevel;
  const inspectionLabel = inspectionLevel
    ? inspectionLevelHelper(inspectionLevel).label
    : null;

  const fields = (
    <Stack gap={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 3 }}>
        <InfoField label="Processor" value={job.vendor?.shop_name} />
        {formattedPhone && <InfoField label="Phone" value={formattedPhone} />}
        {job.vendor?.address?.email && (
          <InfoField label="Email" value={job.vendor?.address?.email} />
        )}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 3 }}>
        <InfoField label="Drop Off Date" value={dropoff} />
        {job.blockTitle && (
          <InfoField label="Drop Off Time" value={job.blockTitle} />
        )}
        {killDate && <InfoField label="Harvest Date" value={killDate} />}
        {cutDate && <InfoField label="Cut Date" value={cutDate} />}
      </Stack>
      <Stack direction="row" gap={3}>
        <InfoField label="Species" value={species} />
        <InfoField label="Count" value={String(job.headCount)} />
        <InfoField label="Inspection" value={inspectionLabel} />
      </Stack>
    </Stack>
  );

  const statusEyebrow = (
    <Box
      sx={{
        px: 2,
        py: 0.75,
        bgcolor: alpha(
          successColor,
          theme.palette.mode === 'dark' ? 0.25 : 0.15,
        ),
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          textTransform: 'uppercase',
          fontWeight: 600,
          color: 'success.main',
          lineHeight: 1.4,
        }}
      >
        {statusLabel}
      </Typography>
    </Box>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* Mobile: stacked layout */}
      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
        {statusEyebrow}
        <Box sx={{ p: 2 }}>{fields}</Box>
      </Box>

      {/* Desktop (>=600px): two-panel layout */}
      <Stack
        direction="row"
        sx={{ display: { xs: 'none', sm: 'flex' }, minHeight: 200 }}
      >
        {/* Left panel */}
        <Stack
          sx={{
            bgcolor: 'primary.main',
            textTransform: 'uppercase',
            px: 3,
            py: 3,
            width: 200,
            flexShrink: 0,
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              variant="h5"
              color="primary.contrastText"
              sx={{ letterSpacing: '0.08em' }}
            >
              Job Details
            </Typography>
            {job.jobId && (
              <Box sx={{ mt: 0 }}>
                <Typography
                  variant="caption"
                  color="primary.contrastText"
                  sx={{ textTransform: 'none', letterSpacing: '0.05em' }}
                >
                  Job ID {job.jobId}
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>

        {/* Right panel with left elevation */}
        <Box
          sx={{
            flex: 1,
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          {statusEyebrow}
          <Box sx={{ p: 3 }}>{fields}</Box>
        </Box>
      </Stack>
    </Card>
  );
}

function InfoField({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  if (!value) return null;
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );
}
